import React from 'react';
import { X, Beaker } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface TdsSampleProperties {
  id: number;
  sample_id: string | null;
  tds_mgl: number | null;
  sample_date: string | null;
  depth_ft: number | null;
  aquifer_name: string | null;
  county: string | null;
  source: string | null;
}

interface Props {
  sample: TdsSampleProperties;
  onClose: () => void;
}

interface Band {
  label: string;
  color: string;
  treatmentTitle: string;
  treatment: string;
  btmFit: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Avoid';
}

function classifyTds(tds: number | null): Band {
  if (tds == null) {
    return { label: 'Unknown', color: '#64748b', treatmentTitle: 'No data',
             treatment: 'TDS not reported for this sample.', btmFit: 'Fair' };
  }
  if (tds < 1000) return {
    label: 'Fresh',
    color: '#3b82f6',
    treatmentTitle: 'No treatment required',
    treatment: 'Potable-quality water. Usable directly for cooling makeup. Primary supply candidate — but likely competes with domestic/agricultural rights and GCD permit thresholds may be tighter.',
    btmFit: 'Excellent',
  };
  if (tds < 3000) return {
    label: 'Slightly brackish',
    color: '#06b6d4',
    treatmentTitle: 'Single-pass RO',
    treatment: 'Standard single-pass RO produces potable water. ~80% recovery. Small skid (~$100–200K installed). Ideal BTM supply — outside fresh water rights pressure in most districts.',
    btmFit: 'Excellent',
  };
  if (tds < 10000) return {
    label: 'Moderately brackish',
    color: '#14b8a6',
    treatmentTitle: 'Single-pass RO + pretreatment',
    treatment: 'BRACS sweet spot. Single-pass RO with anti-scalant dosing. 60–75% recovery. ~$150–400K installed. Brine disposal via nearby Class II SWD typical. Core strategy for BTM cooling.',
    btmFit: 'Good',
  };
  if (tds < 35000) return {
    label: 'Highly brackish',
    color: '#f59e0b',
    treatmentTitle: 'High-pressure RO or 2-pass',
    treatment: 'Seawater-tier RO or two-pass system. Lower recovery (45–60%). Higher capex and opex. Economics marginal for a 1 AFY load unless fresh/brackish alternatives are unavailable.',
    btmFit: 'Fair',
  };
  return {
    label: 'Brine',
    color: '#ef4444',
    treatmentTitle: 'Thermal / MVC required',
    treatment: 'Beyond practical RO. Requires mechanical vapor compression or multi-effect distillation. Capex and energy costs are prohibitive for site-level cooling supply at this scale. Not a realistic source — look elsewhere.',
    btmFit: 'Avoid',
  };
}

const BTM_FIT_COLORS: Record<Band['btmFit'], string> = {
  Excellent: '#16a34a',
  Good: '#0f766e',
  Fair: '#d97706',
  Poor: '#dc2626',
  Avoid: '#dc2626',
};

function formatDate(raw: string | null): string | null {
  if (!raw) return null;
  const s = String(raw);
  // Accept both ISO and date-only
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

export const TdsSampleDetail: React.FC<Props> = ({ sample, onClose }) => {
  const { theme } = useTheme();
  const tds = sample.tds_mgl != null ? Number(sample.tds_mgl) : null;
  const band = classifyTds(tds);
  const fitColor = BTM_FIT_COLORS[band.btmFit];
  const sampleDate = formatDate(sample.sample_date);
  const sampleAge = sampleDate ? new Date().getFullYear() - parseInt(sampleDate.slice(0, 4), 10) : null;

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Beaker size={18} color={band.color} />
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>
              Water Quality Sample
            </h2>
            <div style={{ fontSize: '10px', color: theme.textSecondary, marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Total dissolved solids (TDS)
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* TDS hero */}
        <div style={{
          padding: '14px', marginBottom: '16px', borderRadius: '8px',
          background: band.color + '15', border: `1px solid ${band.color}40`,
        }}>
          <div style={{ fontSize: '10px', fontWeight: 600, color: band.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            TDS
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: band.color, lineHeight: 1.1 }}>
            {tds != null ? `${tds.toLocaleString()} mg/L` : '—'}
          </div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: band.color, marginTop: '4px' }}>
            {band.label}
          </div>
          {sample.aquifer_name && (
            <div style={{ fontSize: '12px', color: theme.text, marginTop: '6px' }}>
              {sample.aquifer_name} aquifer
            </div>
          )}
        </div>

        {/* Well facts */}
        <Section title="Sample Details">
          {sample.sample_id && <Field label="Well ID" value={sample.sample_id} />}
          {sample.depth_ft != null && <Field label="Well Depth" value={`${Number(sample.depth_ft).toLocaleString()} ft`} />}
          {sampleDate && (
            <Field
              label="Sampled"
              value={sampleAge != null && sampleAge > 0 ? `${sampleDate} (${sampleAge}y ago)` : sampleDate}
            />
          )}
          {sample.county && <Field label="County" value={sample.county} />}
          {sample.aquifer_name && <Field label="Aquifer" value={sample.aquifer_name} />}
        </Section>

        {/* Treatment / BTM implications */}
        <Section title="Treatment & BTM Fit">
          <div style={{
            padding: '10px', borderRadius: '6px', marginBottom: '8px',
            background: band.color + '10', border: `1px solid ${band.color}30`,
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px',
            }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: band.color }}>
                {band.treatmentTitle}
              </div>
              <div style={{
                fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
                color: '#fff', background: fitColor,
              }}>
                {band.btmFit}
              </div>
            </div>
            <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.6 }}>
              {band.treatment}
            </div>
          </div>
        </Section>

        {/* Staleness warning */}
        {sampleAge != null && sampleAge > 20 && (
          <Section title="Data Age">
            <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.6 }}>
              This sample is {sampleAge} years old. TDS in unconfined aquifers can shift over time with
              recharge, pumping, and produced-water migration. Treat as directional, not dispositive.
              Pull a fresh sample during site DD before sizing treatment capex.
            </div>
          </Section>
        )}

        {/* Source */}
        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            TWDB Groundwater Database (GWDB) — most recent TDS sample per well.
          </div>
        </Section>
      </div>
    </div>
  );
};
